import { PlusIcon, TrashIcon } from "@xola/icons";
import { flash, FormGroup, Label, Modal, Select, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import {
    FormField,
    handleSuccessfulSubmission,
    InputField,
    isAdjustmentLineItem,
    PurchaseItemStatus,
    PurchasePriceStrategy,
    requiredNumberButNotZero,
    requiredString,
} from "common";
import { FieldArray, useFormikContext } from "formik";
import { isNumber, isUndefined } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import SimpleBar from "simplebar-react";
import { Except } from "type-fest";
import {
    LineItem,
    LineItemCode,
    LineItemDestination,
    LineItemObject,
    LineItemType,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
} from "types";
import { array, number, object, string } from "yup";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { isPriceConfigurationUpdated } from "../../models/PurchaseItem";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { PaymentBreakdown, PriceStrategy } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { shouldRetainPriceByDefault } from "@/models/Seller";

interface FormValues {
    lineItems: Array<Partial<LineItem>>;
}

interface EditCustomLineItemListModalProps {
    purchase: Purchase;
    item: PurchaseItem;
    isOpen: boolean;
    invalidateOnClose?: boolean;
    onClose: (purchase?: Purchase) => void;
}

export const EditCustomLineItemListModal = ({
    purchase,
    item,
    isOpen,
    invalidateOnClose,
    onClose,
}: EditCustomLineItemListModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <Modal.Header>Custom Line Items</Modal.Header>
            <ModalContent purchase={purchase} item={item} invalidateOnClose={invalidateOnClose} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({
    purchase,
    item,
    invalidateOnClose = true,
    onClose,
}: Except<EditCustomLineItemListModalProps, "isOpen">) => {
    const [selectedItem, setSelectedItem] = useState<PurchaseItem>(item);
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    const adjustments = selectedItem.lineItems.filter((lineItem) => {
        return isAdjustmentLineItem(lineItem) && lineItem.code !== LineItemCode.PRIVATE_UPGRADE;
    });
    const [lineItems, setLineItems] = useState(adjustments);
    const [formLineItems, setFormLineItems] = useState<Array<Partial<LineItem>>>();
    const invalidate = useInvalidate();
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());

    const isItemCanceled = selectedItem.status === PurchaseItemStatus.CANCELED;
    const selectablePurchaseItems = purchase.items.filter((purchaseItem) => {
        return (
            purchaseItem.status !== PurchaseItemStatus.CANCELED &&
            [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.ORDER, PurchaseItemObject.WARE].includes(
                purchaseItem.object,
            )
        );
    });

    const {
        mutateAsync: modifyCustomItems,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: CustomLineItemsPayload) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
    });

    interface CustomLineItemsPayload {
        ops?: Op[];
        items?: Item[];
        _emailOverrides?: { sendEmailToTraveler: boolean };
    }

    interface Item {
        id: ID;
        _priceStrategy?: string;
        lineItems: Array<Partial<LineItem>>;
    }

    interface Op {
        path: string;
        op: string;
        data: Partial<LineItem>;
    }

    const updatedPurchase = useMemo(() => {
        if (formLineItems?.length === 0 || !formLineItems) {
            return {};
        }

        const ops: Op[] = [];
        const items: Item[] = [];

        for (const lineItem of formLineItems) {
            if (lineItem.includeInAmount && !lineItem.id) {
                ops.push({
                    op: "addLineItem",
                    path: `/items/${selectedItem.id}/lineItems`,
                    data: { ...makeLineItem(lineItem) },
                });
            } else if (lineItem.id && !lineItem.includeInAmount) {
                ops.push({
                    op: "removeLineItem",
                    path: `/items/${selectedItem.id}/lineItems`,
                    data: { id: lineItem.id },
                });
            } else if (lineItem.id && lineItem.includeInAmount && lineItem.price) {
                const initialLineItem = lineItems.find((lineItemItr) => lineItemItr.id === lineItem.id);
                if (initialLineItem !== lineItem) {
                    items.push({
                        id: selectedItem.id,
                        _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                        lineItems: [
                            {
                                id: lineItem.id,
                                name: lineItem.name,
                                price: lineItem.price,
                                quantity: lineItem.quantity,
                                type: lineItem.type,
                            },
                        ],
                    });
                }
            }
        }

        return {
            _emailOverrides: { sendEmailToTraveler: notifyCustomer },
            _priceStrategy: ops.length > 0 && showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
            ops: ops.length > 0 ? ops : undefined,
            items: items.length > 0 ? items : undefined,
        };
    }, [formLineItems, selectedItem.id, notifyCustomer, lineItems, retainPrice, showRetainPriceToggle]);

    const handleSubmit = async () => {
        try {
            const { data: purchase } = await modifyCustomItems(updatedPurchase);
            handleSuccessfulSubmission(() => {
                onClose(purchase);

                if (invalidateOnClose) {
                    invalidate("purchases", purchase.id);
                    invalidate("purchaseItems", item.id);
                }
            });
        } catch {
            flash.danger("Error saving custom line items");
            onClose();
        }
    };

    const initialLineItems = lineItems.map((lineItem) => {
        const { amount, price, quantity } = lineItem;
        if (Number.isFinite(amount) && price === null && quantity === null) {
            return { ...lineItem, price: amount, quantity: 1 };
        }

        return lineItem;
    });

    return (
        <XolaFormik
            initialValues={{ lineItems: lineItems.length > 0 ? initialLineItems : [makeLineItem()] }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setValues }) => (
                <>
                    <Modal.Body className="flex-col space-y-4">
                        <PurchaseItemSelect
                            items={selectablePurchaseItems}
                            value={selectedItem}
                            className="flex-1"
                            onChange={(item: PurchaseItem) => {
                                setSelectedItem(item);
                                const adjustments = item.lineItems.filter((lineItem) => {
                                    return lineItem.type === LineItemType.ADJUSTMENT;
                                });
                                setLineItems(adjustments);
                                setValues({ lineItems: adjustments });
                            }}
                        />
                        <div className="flex space-x-4">
                            <div className="flex-1 space-y-4">
                                {purchase.migratedAt && showRetainPriceToggle ? (
                                    <PurchasePriceStrategy
                                        isChecked={retainPrice}
                                        isVisible={showRetainPriceToggle}
                                        onChange={() => setRetainPrice(!retainPrice)}
                                    />
                                ) : null}

                                <SimpleBar className="p-1" style={{ maxHeight: 500 }}>
                                    <CustomLineItemList
                                        values={values}
                                        disabled={isItemCanceled}
                                        onLineItemChange={setFormLineItems}
                                    />

                                    <FieldArray name="lineItems">
                                        {(helpers) => (
                                            <Tooltip
                                                disabled={!isItemCanceled}
                                                content="You cannot add or modify charges on a canceled purchase"
                                            >
                                                <SecondaryButton
                                                    icon={<PlusIcon />}
                                                    disabled={isItemCanceled}
                                                    onClick={() => helpers.push(makeLineItem())}
                                                >
                                                    Add Line Item
                                                </SecondaryButton>
                                            </Tooltip>
                                        )}
                                    </FieldArray>
                                </SimpleBar>
                            </div>
                            <div className="flex-1">
                                <div className="bg-gray-lighter p-6">
                                    <PaymentBreakdown
                                        isEditable={false}
                                        purchase={{ ...purchase }}
                                        payload={updatedPurchase ?? []}
                                        item={selectedItem}
                                        onUpdate={(purchase) => {
                                            if (isPriceConfigurationUpdated(purchase, item) && !showRetainPriceToggle) {
                                                setShowRetainPriceToggle(true);
                                                setRetainPrice(!retainPrice);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="flex justify-end space-x-4">
                        <NotifyCustomerCheckbox
                            isChecked={notifyCustomer}
                            onChange={(e: any) => setNotifyCustomer(e.target.checked)}
                        />
                        <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>

                        <Tooltip
                            disabled={!isItemCanceled}
                            content="You cannot add or modify charges on a canceled purchase"
                        >
                            <SubmitButton
                                isLoading={isLoading}
                                isSuccess={isSuccess}
                                disabled={isItemCanceled || (!updatedPurchase.items && !updatedPurchase.ops)}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                        </Tooltip>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface CustomLineItemListProps {
    values: FormValues;
    onLineItemChange: (lineItems: Array<Partial<LineItem>>) => void;

    /** If true, all the fields in custom line item group would be disabled */
    disabled?: boolean;
}

const CustomLineItemList = ({ values, onLineItemChange, disabled = false }: CustomLineItemListProps) => {
    const { values: formikValues } = useFormikContext<FormValues>();
    const { lineItems: formLineItems } = formikValues;
    useEffect(() => {
        onLineItemChange(formLineItems);
    }, [onLineItemChange, formLineItems]);

    return (
        <div className="mb-4 space-y-4 divide-y divide-gray-light">
            {values.lineItems.map((lineItem, index) => {
                if (lineItem.includeInAmount) {
                    const customKey = lineItem.id ?? `custom-${index}-${lineItem.type}-${lineItem.object}`;
                    return (
                        <CustomLineItem
                            key={customKey}
                            index={index}
                            isFirst={index === 0}
                            lineItem={lineItem}
                            isLast={values.lineItems.length === index + 1}
                            disabled={disabled}
                        />
                    );
                }

                return null;
            })}
        </div>
    );
};

interface CustomLineItemProps {
    index: number;
    lineItem: Partial<LineItem>;
    isFirst: boolean;
    isLast: boolean;
    disabled?: boolean;
}

const CustomLineItem = ({ index, lineItem, isFirst, isLast, disabled = false }: CustomLineItemProps) => {
    const { setFieldValue } = useFormikContext<FormValues>();
    const price = lineItem.price ?? 0;
    const quantity = lineItem.quantity ?? 0;
    const [multiplier, setMultiplier] = useState(price < 0 ? -1 : 1);
    // if lineItem has a createdAt property, it means that this element was created earlier
    // and we should not allow its name to be changed in this modal.
    const isNameFieldDisabled = !isUndefined(lineItem.createdAt);

    const handleMultiplierChange = (e: any) => {
        const multiplier = Number(e.target.value);
        setMultiplier(multiplier);
        setFieldValue(`lineItems.${index}.price`, Math.abs(price) * multiplier);
    };

    useEffect(() => {
        setFieldValue(`lineItems.${index}.price`, Math.abs(price ?? 0) * multiplier);
    }, [multiplier, price, index, setFieldValue]);

    useEffect(() => {
        // Sync `amount` when `price` or `quantity` changes
        setFieldValue(`lineItems.${index}.amount`, price * quantity);
    }, [price, quantity, index, setFieldValue]);

    useEffect(() => {
        // when we add a new item, it finds it as the last list item and srolles the list to it
        if (isLast) {
            const newElement = document.querySelector(`#lineitem-${index + 1}`);
            newElement?.scrollIntoView({ block: "start", behavior: "smooth" });
        }
    }, [isLast, index]);

    return (
        <div
            className={clsx(isFirst || "pt-4")}
            data-testid={`custom-line-item-list-${index + 1}`}
            id={`lineitem-${index + 1}`}
        >
            <FormField label="Item" name={`lineItems.${index}.name`}>
                <div className="flex space-x-2">
                    <InputField
                        isRequired
                        autoFocus
                        disabled={isNameFieldDisabled || disabled}
                        data-testid="lineItem-name"
                    />

                    <FieldArray name="lineItems">
                        {() => (
                            <SecondaryButton
                                disabled={disabled}
                                onClick={() => {
                                    setFieldValue(`lineItems.${index}.includeInAmount`, false);

                                    // Fixing validation issue for migrated purchases
                                    if (!isNumber(lineItem.price)) {
                                        setFieldValue(`lineItems.${index}.price`, 0);
                                    }
                                }}
                            >
                                <TrashIcon />
                            </SecondaryButton>
                        )}
                    </FieldArray>
                </div>
            </FormField>

            <div className="flex space-x-2">
                <FormGroup className="flex-1">
                    <Label>Type</Label>

                    <Select value={multiplier} disabled={disabled} onChange={handleMultiplierChange}>
                        <option value={1}>Charge</option>
                        <option value={-1}>Discount</option>
                    </Select>
                </FormGroup>

                <FormGroup className="flex-1">
                    <FormField shortLabelError name={`lineItems.${index}.price`} label="Amount">
                        <CurrencyInputField
                            filterZero
                            isRequired
                            data-testid="currency-input-field"
                            disabled={disabled}
                        />
                    </FormField>
                </FormGroup>

                <FormGroup>
                    <Label>Quantity</Label>

                    <QuantitySpinner
                        size="large"
                        value={lineItem.quantity ?? 1}
                        min={1}
                        disabled={disabled}
                        onChange={async (value) => setFieldValue(`lineItems.${index}.quantity`, value)}
                    />
                </FormGroup>
            </div>

            <Switch.Group>
                <Switch
                    isChecked={lineItem.type === LineItemType.TAXED_ADJUSTMENT}
                    disabled={disabled}
                    onChange={async (value: boolean) =>
                        setFieldValue(
                            `lineItems.${index}.type`,
                            value ? LineItemType.TAXED_ADJUSTMENT : LineItemType.ADJUSTMENT,
                        )
                    }
                />

                <Switch.Label className="ml-2">Is Taxed?</Switch.Label>
            </Switch.Group>
        </div>
    );
};

const validationSchema = object({
    lineItems: array().of(
        object().shape({
            name: string().when("includeInAmount", {
                is: true,
                then: requiredString(),
            }),
            price: number().when("includeInAmount", {
                is: true,
                then: requiredNumberButNotZero(),
            }),
        }),
    ),
});

const makeLineItem = (values?: Partial<LineItem>): Partial<LineItem> => {
    return {
        name: "",
        quantity: 1,
        type: LineItemType.ADJUSTMENT,
        includeInAmount: true,
        destination: LineItemDestination.SELLER,
        object: LineItemObject.DEFAULT,
        ...values,
    };
};
